import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useState } from "react";
import { Copy, Check } from "lucide-react";

function CodeBlock({ className, children, ...props }) {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || "");
  const lang = match ? match[1] : "";
  const code = String(children).replace(/\n$/, "");

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!className) {
    return (
      <code className="bg-[#1C2433] text-[#E5FF00] px-1.5 py-0.5 rounded text-[13px] font-mono" {...props}>
        {children}
      </code>
    );
  }

  return (
    <div className="relative group my-3 rounded-lg overflow-hidden border border-[#273142]">
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#1C2433] border-b border-[#273142]">
        <span className="font-mono text-[10px] text-[#475569] uppercase">{lang || "code"}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 font-mono text-[10px] text-[#475569] hover:text-[#E5FF00] transition-colors"
        >
          {copied ? <Check size={11} className="text-[#10B981]" /> : <Copy size={11} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="bg-[#0D1117] p-4 overflow-x-auto">
        <code className={`font-mono text-[13px] leading-relaxed text-[#C9D1D9] ${className}`} {...props}>
          {children}
        </code>
      </pre>
    </div>
  );
}

export default function MarkdownRenderer({ content }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        code: CodeBlock,
        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
        h1: ({ children }) => <h1 className="text-lg font-bold mt-4 mb-2 text-white">{children}</h1>,
        h2: ({ children }) => <h2 className="text-base font-bold mt-3 mb-2 text-white">{children}</h2>,
        h3: ({ children }) => <h3 className="text-sm font-bold mt-3 mb-1 text-white">{children}</h3>,
        ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>,
        li: ({ children }) => <li className="text-sm">{children}</li>,
        blockquote: ({ children }) => (
          <blockquote className="border-l-2 border-[#E5FF00]/40 pl-4 my-2 text-[#94A3B8] italic">{children}</blockquote>
        ),
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#3B82F6] hover:text-[#60A5FA] underline underline-offset-2">
            {children}
          </a>
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto my-3 rounded-lg border border-[#273142]">
            <table className="w-full text-sm">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="bg-[#1C2433] text-[#94A3B8]">{children}</thead>,
        th: ({ children }) => <th className="px-3 py-2 text-left font-mono text-xs uppercase">{children}</th>,
        td: ({ children }) => <td className="px-3 py-2 border-t border-[#273142]">{children}</td>,
        hr: () => <hr className="border-[#273142] my-4" />,
        strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
        em: ({ children }) => <em className="italic text-[#94A3B8]">{children}</em>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}
